const BROWSER_NOTIFICATION_PREFERENCE_PREFIX =
  "carrfields-web-push-enabled:";

export type BrowserNotificationPreference =
  | "enabled"
  | "disabled"
  | "unset";

type PreferenceStorage = Pick<Storage, "getItem" | "setItem">;

export function browserNotificationPreferenceKey(userId: string): string {
  return `${BROWSER_NOTIFICATION_PREFERENCE_PREFIX}${userId}`;
}

export function parseBrowserNotificationPreference(
  value: string | null,
): BrowserNotificationPreference {
  if (value === "true" || value === "enabled") return "enabled";
  if (value === "false" || value === "disabled") return "disabled";
  return "unset";
}

export function browserNotificationPreference(
  userId: string,
  storage: PreferenceStorage = localStorage,
): BrowserNotificationPreference {
  try {
    return parseBrowserNotificationPreference(
      storage.getItem(browserNotificationPreferenceKey(userId)),
    );
  } catch {
    return "unset";
  }
}

export function saveBrowserNotificationPreference(
  userId: string,
  preference: Exclude<BrowserNotificationPreference, "unset">,
  storage: PreferenceStorage = localStorage,
): void {
  try {
    storage.setItem(browserNotificationPreferenceKey(userId), preference);
  } catch {
    // The live browser subscription remains the fallback source of truth.
  }
}

export function browserNotificationStartupAction(
  preference: BrowserNotificationPreference,
  status: string,
): "enable" | "disable" | "none" {
  if (preference === "disabled" && status === "subscribed") return "disable";
  if (
    preference !== "disabled" &&
    (status === "subscribed" ||
      (preference === "enabled" && status === "unsubscribed"))
  )
    return "enable";
  return "none";
}

export function shouldOfferBrowserNotifications(
  preference: BrowserNotificationPreference,
  status: string,
  dismissedForSession = false,
): boolean {
  if (dismissedForSession) return false;

  // A browser-level permission decision is authoritative. If permission is
  // still undecided or has been blocked, explain how to enable it after every
  // new login even if an older device preference marker exists.
  if (status === "prompt" || status === "denied") return true;

  // Keep an explicit Profile-page opt-out authoritative while the browser
  // permission itself remains granted.
  if (preference === "disabled") return false;

  return status === "unsubscribed" || status === "error";
}
